/** Menu « Plus » du PDA : étiquettes, mouvements, lire un code, tuto, appareils, déconnexion. */
import type { ElementType } from 'react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import {
  Building2,
  EyeOff,
  History,
  LogOut,
  Moon,
  ScanLine,
  Smartphone,
  Sun,
  Tag,
  Undo2,
} from 'lucide-react';
import { useConfirm } from '@/components/ConfirmProvider';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useImpersonate } from '@/contexts/ImpersonateContext';
import { canManageDevices, PDA_DEVICES_SETTINGS } from '@/features/pda/pdaAccess';
import { IgnoredCodesDialog } from '@/features/pda/IgnoredCodesDialog';
import { TenantSwitchDialog } from '@/features/pda/TenantSwitchDialog';
import { reactivateAllCodes, useIgnoredCodes } from '@/features/stock-mode';
import { TutorialButton } from '@/features/tutorials';
import { APK_VERSION_NAME } from '@/hooks/useAutoUpdate';
import { cn } from '@/lib/utils';

type MoreEntry = { to: string; label: string; icon: ElementType };

const ENTRIES: readonly MoreEntry[] = [
  { to: '/mode-stock?vue=etiquettes', label: 'Étiquettes à imprimer', icon: Tag },
  { to: '/mouvements', label: 'Mouvements', icon: History },
  { to: '/mode-stock?vue=maj&lire=1', label: 'Lire un code', icon: ScanLine },
];

const rowClass =
  'flex min-h-14 w-full items-center gap-4 rounded-xl px-4 text-left text-base font-medium active:bg-muted';

export type PdaTheme = 'dark' | 'light';

type PdaMoreSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  theme: PdaTheme;
  onTheme: (theme: PdaTheme) => void;
};

function ThemeChoice({ theme, onTheme }: { theme: PdaTheme; onTheme: (theme: PdaTheme) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2 px-2 py-1" role="radiogroup" aria-label="Thème">
      {(
        [
          ['dark', 'Sombre', Moon],
          ['light', 'Clair', Sun],
        ] as const
      ).map(([value, label, Icon]) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={theme === value}
          onClick={() => onTheme(value)}
          className={cn(
            'flex min-h-12 items-center justify-center gap-2 rounded-xl border-2 text-base font-semibold',
            theme === value ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
          )}
        >
          <Icon className="h-5 w-5" /> {label}
        </button>
      ))}
    </div>
  );
}

export function PdaMoreSheet({ open, onOpenChange, theme, onTheme }: PdaMoreSheetProps) {
  const navigate = useNavigate();
  const { userRole, signOut } = useAuth();
  const { isImpersonating, impersonatedCompany, stopImpersonate } = useImpersonate();
  const [switching, setSwitching] = useState(false);
  const [ignoredOpen, setIgnoredOpen] = useState(false);
  const ignored = useIgnoredCodes();
  const confirm = useConfirm();
  const entries = canManageDevices(userRole)
    ? [...ENTRIES, { to: PDA_DEVICES_SETTINGS, label: 'Appareils', icon: Smartphone }]
    : ENTRIES;
  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="pda-more"
        className="max-h-[85vh] overflow-y-auto rounded-t-2xl p-3 pb-6 [&>button:last-child]:hidden"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <SheetTitle className="px-4 pb-1 pt-2 text-lg">Plus</SheetTitle>
        <SheetDescription className="sr-only">Autres fonctions du PDA</SheetDescription>
        <ThemeChoice theme={theme} onTheme={onTheme} />
        <ul className="space-y-1">
          {userRole === 'super_admin' && (
            <li>
              <button
                type="button"
                className={rowClass}
                onClick={() => {
                  onOpenChange(false);
                  setSwitching(true);
                }}
              >
                <Building2 className="h-6 w-6 shrink-0 text-primary" />
                <span className="min-w-0">
                  Changer de commerce
                  {impersonatedCompany && (
                    <span className="block text-sm text-muted-foreground">
                      Actuel : {impersonatedCompany}
                    </span>
                  )}
                </span>
              </button>
            </li>
          )}
          {isImpersonating && (
            <li>
              <button
                type="button"
                className={rowClass}
                onClick={() => {
                  stopImpersonate();
                  go('/mode-stock?vue=maj');
                }}
              >
                <Undo2 className="h-6 w-6 shrink-0 text-primary" /> Revenir à mon commerce
              </button>
            </li>
          )}
          <li>
            <button
              type="button"
              className={rowClass}
              onClick={() => {
                onOpenChange(false);
                setIgnoredOpen(true);
              }}
            >
              <EyeOff className="h-6 w-6 shrink-0 text-primary" />
              Codes ignorés{ignored.length > 0 ? ` (${ignored.length})` : ''}
            </button>
          </li>
          {entries.map((entry) => (
            <li key={entry.to}>
              <button type="button" className={rowClass} onClick={() => go(entry.to)}>
                <entry.icon className="h-6 w-6 shrink-0 text-primary" />
                {entry.label}
              </button>
            </li>
          ))}
          <li>
            <TutorialButton
              tutoId="visionpos-tuto-pda"
              className={`${rowClass} h-auto justify-start border-0`}
            />
          </li>
          <li>
            <button
              type="button"
              className={rowClass}
              onClick={async () => {
                const accepted = await confirm({
                  title: 'Se déconnecter ?',
                  description: 'Il faudra se reconnecter pour utiliser le PDA.',
                  confirmLabel: 'Se déconnecter',
                });
                if (!accepted) return;
                // Les codes ignorés valent jusqu'à la déconnexion.
                reactivateAllCodes();
                void signOut();
              }}
            >
              <LogOut className="h-6 w-6 shrink-0 text-primary" /> Se déconnecter
            </button>
          </li>
        </ul>
        <p className="px-4 pt-3 font-mono text-xs text-muted-foreground">v{APK_VERSION_NAME}</p>
      </SheetContent>
      <TenantSwitchDialog open={switching} onClose={() => setSwitching(false)} />
      <IgnoredCodesDialog open={ignoredOpen} onClose={() => setIgnoredOpen(false)} />
    </Sheet>
  );
}
